import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import utils from 'utils';
import { onlineCheckInStatusSelector, getProfileFound } from 'selectors/checkinSelectors';
import { selectedCountryDetails, trimDialingCodeIfExists, parseCountries, getFormattedValue } from 'utils/phoneInput';
import Storage from 'utils/storageManager';
import { SESSION_STORAGE } from 'constants/session';
import useCountryFieldsConfig from 'hooks/useCountryFieldsConfig';
import { useSelector } from 'react-redux';
import FieldControl from 'components/Form/FieldControl';
import CountryList from 'components/Form/CountryList';
import CUSTOMER_SUPPORT from 'constants/customerSupport';
import { isMasked } from 'utils/validation';

/**
 * PhoneNumberField Description:
 *
 * @param {object} props - React Props
 * @param {string} props.name: unique field name
 * @param {string} props.id - unique field id
 * @param {string} props.label - a descriptive label for the user to read
 * @param {boolean} props.full - will make the field fill the width of its container
 * @param {boolean} props.error - Show Error State
 * @param {boolean} props.attention - Show Attention State
 * @param {Array | boolean} props.validations
 * @param {string | boolean} props.initialValueButton
 * @param {function} props.initialValueButtonHandler - Handler for remove the initial value button
 * @param {string} props.inputType - Input type passed into FieldControl (i.e. 'text', 'number', 'tel')
 * @return {JSX} ...
 */
const PhoneNumberField = ({ inputType, initialValueButton, ...props }) => {
  const uuid = utils.dom.uuid();
  let autoCompleteValue;
  if (props.initialValueButton) {
    autoCompleteValue = `new-${props.name}`;
  } else if (props.autoComplete) {
    autoCompleteValue = props.autoComplete;
  } else {
    autoCompleteValue = 'tel';
  }
  const {
    PHONE_TYPE: { GBO_MAX_PHONE_DIGITS_ALLOWED },
  } = CUSTOMER_SUPPORT;

  const donotFetchStateAndProvinces = true;
  const inputRef = useRef();
  const prevInputRef = useRef();
  const getCountrySelected = utils.phoneInput.selectedCountryDetails.get();

  let initialCountryCode = utils.config.getDomainCountry();

  const [showList, setShowList] = useState(false);

  const isMaskedPhoneNumber = utils.fieldValidation.isMasked(initialValueButton);

  const onlineCheckInStatus = useSelector(onlineCheckInStatusSelector);
  const { ociComplete, driverProfileComplete, contactProfileComplete } = onlineCheckInStatus;

  const isProfileFound = useSelector(getProfileFound);
  const isUnauthPartialProfileFound =
    isProfileFound &&
    ((driverProfileComplete && !contactProfileComplete) || (contactProfileComplete && !driverProfileComplete));

  const foundCompleteProfile = driverProfileComplete && contactProfileComplete;
  const isCompletedDriverContactInfo = foundCompleteProfile && ociComplete;

  const inputPhoneValue = inputRef?.current?.props?.current?.value;

  const defaultValue = useMemo(() => {
    if (initialValueButton && (isUnauthPartialProfileFound || !isCompletedDriverContactInfo) && !isMaskedPhoneNumber) {
      return initialValueButton;
    }
    return inputPhoneValue;
  }, [inputPhoneValue]);

  const [hideCountryFlag, setHideCountryFlag] = useState(isMaskedPhoneNumber);
  const [showCountryFlag, setCountryCountryFlag] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const [formattedNumber, setFormattedNumber] = useState(defaultValue);

  if ((isUnauthPartialProfileFound || !isCompletedDriverContactInfo) && hideCountryFlag && isMaskedPhoneNumber) {
    initialCountryCode = getCountrySelected?.countryCode;
  }

  const {
    selectedCountry,
    setSelectedCountry,
    countries = [],
  } = useCountryFieldsConfig(initialCountryCode, donotFetchStateAndProvinces);

  const { getCountryByCode } = useMemo(
    () => parseCountries(Object.values(countries || {}) || []),
    [!!countries.length]
  );

  const selectedCountryIndex = useMemo(() => {
    if (selectedCountry?.country_code) {
      return countries?.findIndex((item) => item?.country_code === selectedCountry?.country_code);
    }
    return null;
  }, [selectedCountry]);

  const selectedCountryInfo = useMemo(
    () => getCountryByCode(selectedCountryCode),
    [selectedCountryCode, getCountryByCode]
  );

  const { country_content: { dialing_code = null } = {} } = selectedCountry || {};

  const handleShowCountryFlag = (clearInitialValue, e) => {
    if (clearInitialValue) {
      clearInitialValue();
    }
    setHideCountryFlag(!hideCountryFlag);
    setCountryCountryFlag(true);
    e && inputRef?.current?.focus();
  };

  useEffect(() => {
    if (initialValueButton && !hideCountryFlag && !showCountryFlag) {
      handleShowCountryFlag();
    }
  }, [dialing_code, hideCountryFlag, initialValueButton]);

  const countrySessionInfo = selectedCountryDetails.get();

  // useEffect updates the onChange callback state whenever the final form value fails to update as expected

  const updateInputRef = useMemo(() => {
    if (isMasked(defaultValue)) {
      return true;
    }
    return false;
  }, [defaultValue]);

  useEffect(() => {
    // formattedNumber not set but has default input prop value
    // on country change, formatted value was not updating correctly to fix that adding below condition
    if (
      !formattedNumber &&
      defaultValue &&
      !isMasked(defaultValue) &&
      defaultValue !== selectedCountryInfo?.dialingCode &&
      defaultValue !== formattedNumber
    ) {
      let rawPhonenumber = getFormattedValue(defaultValue, selectedCountryInfo);
      rawPhonenumber = !!rawPhonenumber && rawPhonenumber?.replace(/[^0-9+]/g, '');
      if (rawPhonenumber) {
        setFormattedNumber(rawPhonenumber);
      }
    }
    // Edge case where autofill populates the same numeric value for previous and current inputs,
    // with the only variation being a '+' sign. Triggering an onChange callback update to maintain accurate form state.
    if (
      (prevInputRef.current?.value &&
        selectedCountryInfo?.dialingCode &&
        prevInputRef.current?.value !== selectedCountryInfo?.dialingCode &&
        !prevInputRef.current?.value?.startsWith('+')) ||
      (prevInputRef.current?.value?.startsWith('+') &&
        prevInputRef.current?.value?.length > 0 &&
        inputRef.current?.props.current?.value?.trimEnd() === selectedCountryInfo?.dialingCode)
    ) {
      const formattedValue = getFormattedValue(prevInputRef.current.value, selectedCountryInfo);

      if (
        formattedNumber !== formattedValue ||
        (formattedNumber === formattedValue && prevInputRef.current?.value !== formattedNumber)
      ) {
        inputRef.current?.handleChange(formattedValue);
      }
    }
    // set initial prevInputRef current value based on formatted number , if the current value is not set
    if (!prevInputRef.current && !!formattedNumber) {
      prevInputRef.current = {
        formattedValue: formattedNumber,
        value: formattedNumber,
        sameAsPrevValue: false,
      };
    }

    if (updateInputRef && isMasked(defaultValue) && !formattedNumber && formattedNumber !== '') {
      setFormattedNumber(defaultValue);
    }
  }, [formattedNumber, defaultValue, updateInputRef]);

  // useEffect updates the sessionStorage country value whenever the selected country changes
  useEffect(() => {
    if (selectedCountryCode !== selectedCountry?.country_code) {
      setSelectedCountryCode(selectedCountry?.country_code);
    }
    if (selectedCountryInfo) {
      Storage.SessionStorage.set(SESSION_STORAGE.COUNTRY_SELECTED, selectedCountryInfo, true);
    }
  }, [selectedCountry, selectedCountryInfo, countrySessionInfo]);

  const showDialingCode = useMemo(() => {
    if (hideCountryFlag && isMaskedPhoneNumber) return '';

    return true;
  }, [selectedCountry, hideCountryFlag]);

  const handleSelectCountry = (country) => {
    setShowList(false);
    setSelectedCountry(country);

    setTimeout(() => {
      inputRef?.current?.focus();
      inputRef.current?.handleChange(formattedNumber || '');
    }, 10);
  };

  // function to format phonenumber, with allowed phone number of digits
  const isValidPhoneNumber = (phoneNumber) => {
    if (isMasked(phoneNumber)) {
      return phoneNumber;
    }
    const allowFormattedPhoneNumberCharacters = /^[ 0-9()-]*$/g;
    const allowOnlyNumbers = /^[0-9]*$/g;

    const hasPhoneNumberFormat = selectedCountryInfo?.phoneNumberFormat;
    const allowedCharacters = hasPhoneNumberFormat ? allowFormattedPhoneNumberCharacters : allowOnlyNumbers;

    return phoneNumber?.match(allowedCharacters)?.length > 0;
  };

  // callback functoin to handle  form input change
  const updatedPhoneInfo = (e) => {
    // grab phone info from handleChange
    const value = e?.target && Object.hasOwn(e.target, 'value') ? e.target.value : e;
    if (selectedCountryInfo) {
      let formattedValue = value;
      if (!isMasked(formattedValue)) {
        formattedValue = getFormattedValue(formattedValue, selectedCountryInfo);
        formattedValue = formattedValue.replace(/[- )(]/g, '');
      }

      if (isValidPhoneNumber(formattedValue)) {
        props?.onChange?.({
          country: selectedCountryInfo,
          phone_number: formattedValue,
        });

        if (prevInputRef.current?.formattedValue === formattedValue && !prevInputRef.current?.sameAsPrevValue) {
          setFormattedNumber(value);
        } else {
          setFormattedNumber(formattedValue);
        }
        // storing ref value,for reference if autofill populates the same numeric value for previous and current inputs
        prevInputRef.current = {
          formattedValue,
          value,
          sameAsPrevValue: prevInputRef.current?.formattedValue === formattedValue,
        };
      }
    }
  };

  const countryListId = `country-list-${props.name}`;

  // Restrict phone field to max 14
  const maxLength = () => {
    const maxDigits = `${selectedCountry?.country_content?.dialing_code} `.length + GBO_MAX_PHONE_DIGITS_ALLOWED;
    return maxDigits;
  };

  const trimmedValue = (value) => trimDialingCodeIfExists(selectedCountryInfo, value);
  // Formats the input value through React Final Form's `format` prop (e.g., for phone number formatting)
  const handleFormatPhone = (value = '') => {
    if (isMasked(value) || !selectedCountryInfo?.dialingCode) {
      return value;
    }

    const phoneNumber = selectedCountryInfo?.formatPhoneNumber(trimmedValue(value));
    return `${showDialingCode ? selectedCountryInfo?.dialingCode : ''} ${phoneNumber || ''}`;
  };

  return (
    <>
      <FieldControl
        {...props}
        ref={inputRef}
        id={props.id || uuid}
        type={inputType}
        autoComplete={autoCompleteValue}
        className='phone-number-field'
        initialValueButton={hideCountryFlag && isMaskedPhoneNumber && initialValueButton}
        initialValueButtonHandler={handleShowCountryFlag}
        aria-owns={countryListId}
        aria-controls={countryListId}
        maxLength={maxLength()}
        inputMode='numeric'
        tabbingIndex={!isMaskedPhoneNumber}
        onChange={updatedPhoneInfo}
        applyInputPropsToChildIndex={1}
        {...(inputRef.current?.props?.current?.value?.length <= 0 &&
          selectedCountry?.country_content?.dialing_code && {
            initialValue: selectedCountry?.country_content?.dialing_code,
          })}
        {...(props.required && {
          validate: (value) => utils.phoneInput.checkPhoneCode(value), // manually adding a validation, as validations are not triggere when we are being used with format phone number. This happens only with functional component.
        })}
        {...(!props.required && {
          validate: (value) => utils.phoneInput.altPhoneNumberValidation(value), /// /manually adding a validation, as validations are not triggere when we are being used with format phone number. This happens only with functional component.
        })}
        {...(selectedCountry?.country_content?.dialing_code && {
          format: (value) => handleFormatPhone(formattedNumber || value),
        })}
      >
        {showDialingCode ? (
          <CountryList
            showList={showList}
            setShowList={setShowList}
            countries={countries}
            selectedCountry={selectedCountry}
            handleSelectCountry={handleSelectCountry}
            ariaLabeledBy={props.id || uuid}
            selectedCountryIndex={selectedCountryIndex}
            id={countryListId}
            uniqueId={uuid}
          />
        ) : (
          <></>
        )}
        <input />
      </FieldControl>
    </>
  );
};
PhoneNumberField.defaultProps = {
  inputType: 'phone',
};

PhoneNumberField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  full: PropTypes.bool,
  error: PropTypes.bool,
  attention: PropTypes.bool,
  validations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  initialValueButton: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  initialValueButtonHandler: PropTypes.func,
  inputType: PropTypes.string,
  onChange: PropTypes.func,
};

export default PhoneNumberField;
